import { useLang } from '../i18n/LanguageContext'
import './PricingPage.css'
import { useSubscriptionPlans } from '../hooks/useSubscriptionPlans'

function formatStorage(megabytes) {
  return megabytes >= 1024 ? `${megabytes / 1024} GB` : `${megabytes} MB`;
}

/**
 * The list under a plan, built from what the plan has (its limits and flags from the platform), and only from that.
 * Nothing is added here that the plan does not carry: a line that is not in the data is a promise nobody has made.
 * A limit that is not set means no limit.
 */
function generateFeatures(plan, t) {
  const features = [];
  const limits = plan.limits;

  if (limits) {
    features.push(limits.maxMembers == null
      ? t('pricing.unlimitedMembers')
      : t('pricing.upToMembers', { count: limits.maxMembers }));
    features.push(limits.maxBookingsPerMonth == null
      ? t('pricing.unlimitedBookings')
      : t('pricing.bookingsPerMonth', { count: limits.maxBookingsPerMonth }));
    features.push(limits.maxTicketsPerMonth == null
      ? t('pricing.unlimitedTickets')
      : t('pricing.ticketsPerMonth', { count: limits.maxTicketsPerMonth }));
    features.push(limits.maxLocations == null
      ? t('pricing.unlimitedLocations')
      : t('pricing.locations', { count: limits.maxLocations }));
    if (limits.maxStorageMb != null) {
      features.push(t('pricing.storage', { size: formatStorage(limits.maxStorageMb) }));
    }
    if (limits.hasCustomBranding) {
      features.push(t('pricing.customBranding'));
    }
    if (limits.hasExportAccess) {
      features.push(t('pricing.exportCsv'));
    }
    if (limits.hasPrioritySupport) {
      features.push(t('pricing.prioritySupport'));
    }
  }

  features.push(t('pricing.emailSms'));
  return features;
}

/**
 * Get description for plan
 */
function getPlanDesc(planKey, t) {
  const descMap = {
    'free': t('pricing.planDescFree'),
    'standard': t('pricing.planDescStandard'),
    'avansert': t('pricing.planDescAdvanced')
  };
  return descMap[planKey] || '';
}

/**
 * Determine if plan is "popular"
 */
function isPlanPopular(planKey) {
  return planKey === 'standard';
}

function PricingPage() {
  const { plans, loading, error } = useSubscriptionPlans();
  const { t } = useLang();
  const faq = t('pricing.faq');

  // Show loading state
  if (loading && plans.length === 0) {
    return (
      <div className="pricing-page">
        <section className="hero">
          <div className="container">
            <h1 className="hero-title" style={{textAlign: 'center'}}>
              {t('pricing.heroTitle')}
            </h1>
            <p className="hero-subtitle" style={{textAlign: 'center', maxWidth: '500px', margin: '0 auto'}}>
              {t('pricing.loading')}
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <section className="hero">
        <div className="container">
          <h1 className="hero-title" style={{textAlign: 'center'}}>
            {t('pricing.heroTitle')}
          </h1>
          <p className="hero-subtitle" style={{textAlign: 'center', maxWidth: '500px', margin: '0 auto'}}>
            {t('pricing.heroSubtitle')}
          </p>
        </div>
      </section>

      <section className="section pricing-section">
        <div className="container">
          <div className="alert alert-success" style={{marginBottom: 'var(--space-4)', textAlign: 'center'}}>
            {t('pricing.introNotice')}
          </div>
          {error && (
            <div className="alert alert-warning" style={{marginBottom: 'var(--space-4)', textAlign: 'center'}}>
              {t('pricing.loadError')}
            </div>
          )}
          <div className="pricing-grid">
            {plans.map((plan) => (
              <div
                key={plan.planKey}
                className={`pricing-card ${isPlanPopular(plan.planKey) ? 'popular' : ''}`}
              >
                {isPlanPopular(plan.planKey) && <div className="popular-badge">{t('pricing.popular')}</div>}
                <h3 className="pricing-name">{plan.name}</h3>
                <div className="pricing-price">
                  <span className="price-amount">{plan.monthlyPrice}</span>
                  <span className="price-unit">{t('pricing.perMonth')}</span>
                </div>
                <p className="pricing-desc">{plan.description || getPlanDesc(plan.planKey, t)}</p>
                <ul className="pricing-features">
                  {generateFeatures(plan, t).map((feature, j) => (
                    <li key={j}><span>✓</span> {feature}</li>
                  ))}
                </ul>
                <a
                  href={`/signup?plan=${encodeURIComponent(plan.planKey)}`}
                  className={`btn ${isPlanPopular(plan.planKey) ? 'btn-primary' : 'btn-outline'} btn-lg`}
                >
                  {plan.monthlyPrice === 0 ? t('pricing.ctaFree') : t('pricing.ctaTrial')}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section faq-section">
        <div className="container">
          <h2 className="section-title" style={{marginBottom: 'var(--space-8)'}}>{t('pricing.faqTitle')}</h2>
          <div className="faq-grid">
            {faq.map((item, i) => (
              <div key={i} className="faq-item">
                <h4>{item.q}</h4>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default PricingPage
